import classNames from 'classnames';
import React from 'react';
import UDText from 'ud-ui/components/text';

export interface UDEmptyStateProps {
  /**
   * Иконка над заголовком — узел (обычно иконка lucide размером 28). Кит рисует её
   * в круглой подложке; доменных иконок он не знает, их передаёт экран.
   */
  icon?: React.ReactNode;
  /**
   * Заголовок. Строка и узел (`<Trans>`, фрагмент со ссылкой) рисуются типографикой
   * набора; готовый `UDText` — как есть, это явный выбор своей типографики.
   */
  title?: React.ReactNode;
  /** Пояснение под заголовком. Правило то же, что и для заголовка. */
  subtitle?: React.ReactNode;
  /** Действие: кнопка или несколько. */
  action?: React.ReactNode;
  className?: string;
}

const isEmpty = (value: React.ReactNode) => value === undefined || value === null || value === false || value === '';

const isOwnText = (value: React.ReactNode) => React.isValidElement(value) && value.type === UDText;

const renderText = (value: React.ReactNode, node: React.ReactElement) => {
  if (isEmpty(value)) {
    return null;
  }

  return isOwnText(value) ? value : node;
};

/**
 * Пустое состояние: колонка по центру из иконки, заголовка, пояснения и действия.
 *
 * Один компонент на все экраны «здесь пока ничего нет». Текст центрируется: многострочное
 * пояснение, выровненное влево внутри центрированной колонки, выглядит поломкой.
 */
const UDEmptyState = ({ icon, title, subtitle, action, className }: UDEmptyStateProps) => {
  return (
    <div className={classNames('flex flex-col items-center text-center', className)}>
      {!isEmpty(icon) && (
        <div
          className="bg-surfaceSunken rounded-pill text-inkSecondary mb-4 flex size-14 items-center justify-center"
          data-testid="empty-state-icon"
          aria-hidden
        >
          {icon}
        </div>
      )}

      {renderText(
        title,
        <UDText as="h2" variant="body-1" className="mb-2">
          {title}
        </UDText>,
      )}

      {renderText(
        subtitle,
        <UDText variant="title-4" className="mb-6">
          {subtitle}
        </UDText>,
      )}

      {action}
    </div>
  );
};

export default UDEmptyState;
